/**
 * Browser SpeechSynthesis wrapper that respects the parent-controlled
 * settings (voice, rate, mute). Designed for "speak this single word/
 * sentence" use cases — flash card audio, first-pass cues, etc.
 *
 * Spec §6: "All spoken content is rendered via the browser's Speech
 * Synthesis API in v1. Pre-recorded audio is not used... but the
 * audio layer should be designed so that recorded audio for specific
 * words can be substituted later if Speech Synthesis mispronounces an
 * important word." Honour that by keeping the call site (`speak(text,
 * settings)`) opaque about the underlying mechanism.
 *
 * Voice-loading race (Chrome + Safari):
 *   `speechSynthesis.getVoices()` returns `[]` on first call until the
 *   browser fires `voiceschanged`. If we just speak() with no voice
 *   resolution, the first utterance falls back to the system default
 *   even when parent settings specify a different one — Eva would
 *   hear voice A on tap 1 and voice B (the configured one) on tap 2.
 *
 *   Fix has two parts:
 *     1. Kick voice loading as a side effect on module import — the
 *        getVoices() call itself triggers Chrome's voice fetch.
 *     2. speak() awaits a one-shot promise that resolves on
 *        `voiceschanged` (or instantly if voices already populated).
 *
 *   speak() returns a Promise<void> so callers *can* await if they need
 *   to, but the existing fire-and-forget call sites still work — the
 *   discarded promise just resolves later.
 */
import type { Settings } from "@/types/data";

export type SpeechSettings = Pick<Settings, "speechVoice" | "speechRate" | "audioEnabled">;

// Side effect on import: in Chrome, getVoices() is what triggers the
// initial voice fetch. Calling it early means by the time we need the
// fallback path the voices list is populated. (Cloud TTS doesn't need
// voices loaded — it's all server-side.)
if (typeof window !== "undefined" && window.speechSynthesis) {
  window.speechSynthesis.getVoices();
}

// ─── Public surface ──────────────────────────────────────────────────

/**
 * Speak a phrase. Honours `audioEnabled = false` by no-op'ing —
 * callers don't need a separate branch for muted state.
 *
 * Routing:
 *   1. **Cloud TTS first** (`/api/tts` → Google Cloud Neural2). Gives
 *      a naturalistic en-GB voice on every device, sidestepping the
 *      iOS Web Speech limitation where Premium / Siri voices aren't
 *      exposed to web pages. Free tier covers Eva's usage easily.
 *   2. **Web Speech fallback**. Used when the cloud endpoint isn't
 *      configured (no GOOGLE_TTS_API_KEY secret), times out, or
 *      returns an error. Honours the configured voiceURI via the
 *      resolver ladder.
 *
 * The fallback path means the app stays functional offline-ish (eg
 * Wi-Fi blip) — Eva still hears the word, just in the compact voice
 * — so cloud-TTS-down isn't a blocking failure.
 */
export async function speak(text: string, settings: SpeechSettings): Promise<void> {
  if (!settings.audioEnabled) return;
  if (typeof window === "undefined") return;
  cancelSpeech();

  if (await speakCloud(text, settings)) return;

  speakWebSpeech(text, settings);
}

/**
 * Cancel any in-flight speech, regardless of which path is playing.
 * Both the cloud-audio element and the SpeechSynthesis queue need
 * clearing; we don't track which is active to keep the public API
 * simple.
 */
export function cancelSpeech(): void {
  if (typeof window === "undefined") return;
  window.speechSynthesis?.cancel();
  cancelCloudPlayback();
}

// ─── Cloud TTS path ──────────────────────────────────────────────────

/** In-memory cache: rate-keyed audio blob URLs for previously spoken
 *  text. Same word at same rate plays from cache; saves both latency
 *  and TTS API calls. Lives for the page lifetime. */
const cloudCache = new Map<string, string>();
let cloudAvailable = true; // flips to false on 503 to skip future fetches

/**
 * Singleton <audio> element reused for every cloud-TTS playback. Why
 * one element instead of `new Audio()` per word: on iPad WebKit, every
 * newly-created HTMLAudioElement requires its FIRST .play() to happen
 * inside a user gesture, or iOS silently refuses. Auto-play (the
 * effect that fires when a flash card appears) runs well after the
 * gesture window closed, so per-word elements stop working after the
 * first one or two. A single reused element only needs gesture-unlock
 * once; subsequent src changes + play() calls work freely.
 */
let cloudAudio: HTMLAudioElement | null = null;
function ensureCloudAudio(): HTMLAudioElement {
  if (!cloudAudio) cloudAudio = new Audio();
  return cloudAudio;
}

/**
 * Try to fetch + play via cloud TTS. Returns true on success, false
 * if we should fall back to Web Speech. Failures: server returns
 * 503 (key not configured) → cache that and skip cloud forever this
 * session; other errors → try Web Speech this once but stay
 * opportunistic for next call.
 */
async function speakCloud(text: string, settings: SpeechSettings): Promise<boolean> {
  if (!cloudAvailable) return false;
  const cacheKey = `${settings.speechRate}:${text}`;
  let blobUrl = cloudCache.get(cacheKey);

  if (!blobUrl) {
    try {
      const res = await fetch("/api/tts", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, rate: settings.speechRate }),
      });
      if (res.status === 503) {
        // Server isn't configured for cloud TTS. Stop trying this
        // session — the next page load re-checks if the secret has
        // been set.
        cloudAvailable = false;
        return false;
      }
      if (!res.ok) return false;
      const blob = await res.blob();
      blobUrl = URL.createObjectURL(blob);
      cloudCache.set(cacheKey, blobUrl);
    } catch {
      return false;
    }
  }

  try {
    const audio = ensureCloudAudio();
    // Setting src on a playing element stops current playback and
    // starts loading the new resource — equivalent to a manual
    // pause + src + load, but in one step. Reset currentTime so
    // re-plays of a cached word start from the beginning.
    audio.src = blobUrl;
    audio.currentTime = 0;
    await audio.play();
    return true;
  } catch {
    return false;
  }
}

function cancelCloudPlayback(): void {
  if (cloudAudio && !cloudAudio.paused) {
    cloudAudio.pause();
    cloudAudio.currentTime = 0;
  }
}

// ─── Web Speech fallback ─────────────────────────────────────────────

function speakWebSpeech(text: string, settings: SpeechSettings): void {
  const synth = window.speechSynthesis;
  if (!synth) return;
  // Voice readiness can lag on iOS / Chromium; for the fallback path
  // we use whatever's available immediately rather than awaiting,
  // since the cloud path already covered the "perfect voice" case.
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = settings.speechRate;
  const voice = resolveVoice(synth.getVoices(), settings.speechVoice);
  if (voice) utterance.voice = voice;
  synth.speak(utterance);
}

/**
 * Pick the best available voice for the configured value, with
 * cross-device fallbacks.
 *
 * Why this needs fallbacks: parent settings store the voice as a
 * `voiceURI` (e.g. "Google UK English Female" on Chromium), but the
 * voice catalogue differs per OS/browser. Apple devices have their
 * own URIs (`com.apple.ttsbundle.siri_*`), Android another set
 * again. A strict URI match — what the old code did — silently fell
 * back to the system default on any device that wasn't the one the
 * voice was originally picked on.
 *
 * Resolution ladder, first match wins:
 *   1. Exact voiceURI match — the original behaviour.
 *   2. Exact name match — voice *names* are more portable across
 *      browsers than URIs.
 *   3. Curated en-GB female ladder — if the configured value looks
 *      like a UK English Female descriptor, walk a hand-picked list
 *      of female en-GB voices known across browsers/OSes (Kate /
 *      Serena on iOS, Hazel on Windows, etc.) before falling
 *      through. This is what catches iPad Chrome — without it the
 *      next step picks Daniel (male) which isn't what Matt wants.
 *   4. Same language prefix — first voice with matching lang.
 *   5. First English voice of any locale.
 *   6. Null — let SpeechSynthesis pick the system default.
 *
 * Exported so the ParentSettings UI can show the resolved voice in
 * the dropdown rather than the literal stored URI (which on iPad
 * shows as "Device default" while the audio plays Kate — visually
 * broken even though audio is fine).
 */
export function resolveVoice(
  voices: SpeechSynthesisVoice[],
  configured: string | null,
): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null;

  // Configured = null means "device default" — caller's explicit
  // choice, don't override.
  if (configured === null) return null;

  // 1. Exact voiceURI match.
  const byUri = voices.find((v) => v.voiceURI === configured);
  if (byUri) return byUri;

  // 2. Exact name match. Some browsers expose `name` differently from
  // `voiceURI`; the Google voices on Chromium have name == URI but on
  // Safari the URIs are scheme-prefixed while names are plain.
  const byName = voices.find((v) => v.name === configured);
  if (byName) return byName;

  // 3. Curated en-GB female ladder — handles the "Eva picked Google
  // UK English Female on Mac, now she's on iPad" case explicitly.
  // Walks the named list first, then prefers voices whose URI or
  // name marks them as a higher-tier variant (Siri / Premium /
  // Enhanced) over the compact defaults.
  if (looksLikeEnGbFemale(configured)) {
    const female = findByCuratedNames(voices, EN_GB_FEMALE_PRIORITY);
    if (female) return female;
    const enhanced = pickBestEnGbFemale(voices);
    if (enhanced) return enhanced;
  }

  // 4. Same language. Infer language from the configured string —
  // accurate for the "Google UK English ..." family, ambiguous for
  // others. We deliberately only handle the en-GB / en-US split here.
  const wantedLang = inferLangFromVoiceName(configured);
  if (wantedLang !== null) {
    const byLang = voices.find((v) => v.lang.toLowerCase() === wantedLang);
    if (byLang) return byLang;
    // Loose match — "en-GB" wanted, accept any "en-GB-*".
    const byLangPrefix = voices.find((v) =>
      v.lang.toLowerCase().startsWith(wantedLang + "-"),
    );
    if (byLangPrefix) return byLangPrefix;
  }

  // 5. First English voice of any locale.
  const anyEnglish = voices.find((v) => v.lang.toLowerCase().startsWith("en"));
  if (anyEnglish) return anyEnglish;

  // 6. System default.
  return null;
}

/**
 * Names of en-GB-leaning female voices in priority order. First entry
 * wins if present. Mostly Apple's iOS catalogue (the real cross-
 * platform pain point) but includes the Chromium and Windows Hazel
 * variants so desktop also picks something good even if the exact URI
 * differs from what was saved.
 *
 * Curated by hand; expand if QA on a new device shows the wrong
 * gender voice being picked. The hard rule is the entries must be
 * female and en-GB (or close — Karen/Samantha are last-resort fall-
 * throughs to any female English voice rather than a male en-GB).
 */
/**
 * Names of en-GB-leaning female voices in priority order. First entry
 * wins if present. Order top-to-bottom is *quality* — Siri voices on
 * iOS 17+ sound very natural, followed by Microsoft's Online "Natural"
 * voices, then Premium/Enhanced compact variants, then the older
 * compact voices as a last resort.
 *
 * iOS Siri voices (downloaded by parent in iOS Settings → Accessibility
 * → Spoken Content → Voices → English UK) appear in this list with
 * names like "Siri Voice 1" or "Siri Voice 3"; the four numbered ones
 * cover the standard English-UK Siri set.
 *
 * Expand if QA on a new device shows the wrong voice being picked.
 */
const EN_GB_FEMALE_PRIORITY: ReadonlyArray<string> = [
  // Matt's chosen default. "Serena" matches "Serena", "Serena (Premium)"
  // and "Serena (Enhanced)" via findByCuratedNames's startsWith logic,
  // and the matches are sorted by tier so Premium wins when multiple
  // are installed.
  "Serena",
  // Other AI-generated voices on iOS 17+ (Siri voices), downloaded by user.
  "Siri Voice 4",
  "Siri Voice 3",
  "Siri Voice 2",
  "Siri Voice 1",
  // Microsoft "Online (Natural)" — neural voices on Edge.
  "Microsoft Sonia Online (Natural) - English (United Kingdom)",
  "Microsoft Libby Online (Natural) - English (United Kingdom)",
  "Microsoft Hazel Online (Natural) - English (United Kingdom)",
  // Chromium / Chrome desktop.
  "Google UK English Female",
  // Microsoft non-online (Windows / Edge Chromium).
  "Microsoft Hazel - English (Great Britain)",
  "Microsoft Susan - English (Great Britain)",
  "Hazel",
  "Libby",
  "Sonia",
  // Other Apple en-GB female voices — fallback if Serena isn't installed.
  "Kate",
  "Martha",
  "Stephanie",
  // Last-resort female English fallbacks (not en-GB but female).
  "Karen", // en-AU female
  "Samantha", // en-US female
  "Moira", // en-IE female
];

/**
 * If the curated name list misses, try a tier-aware scan: prefer
 * voices whose URI or name marks them as Premium/Enhanced/Siri over
 * compact variants. Apple's voiceURI follows
 * `com.apple.voice.{compact,enhanced,premium}.<lang>.<name>`; Siri
 * voices include "siri" in the URI. We use those markers to rank.
 */
function pickBestEnGbFemale(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  const enGbFemale = voices.filter((v) => {
    const langOk = v.lang.toLowerCase().startsWith("en-gb");
    if (!langOk) return false;
    const name = v.name.toLowerCase();
    // Heuristic — Apple voice names are usually first-name-only, so
    // we whitelist known female names. (Apple doesn't expose a
    // gender property.)
    const knownFemale = /\b(kate|serena|martha|stephanie|hazel|libby|sonia|susan)\b/.test(name);
    const sayLabelledFemale = name.includes("female");
    return knownFemale || sayLabelledFemale;
  });
  if (enGbFemale.length === 0) return null;
  enGbFemale.sort((a, b) => tier(b) - tier(a));
  return enGbFemale[0];
}

function tier(v: SpeechSynthesisVoice): number {
  const uri = v.voiceURI.toLowerCase();
  const name = v.name.toLowerCase();
  if (uri.includes("siri") || name.includes("siri")) return 4;
  if (uri.includes("premium") || name.includes("premium") || name.includes("natural")) return 3;
  if (uri.includes("enhanced") || name.includes("enhanced")) return 2;
  return 1;
}

/**
 * For each curated target, gather every voice that matches by name or
 * voiceURI, then pick the highest-tier variant (Premium > Enhanced >
 * compact). This is what lets "Serena" in the priority list resolve
 * to "Serena (Premium)" when the user has it installed alongside the
 * compact version — Premium wins.
 *
 * Match shapes:
 *   - Exact name (case-insensitive) — covers "Kate" → "Kate".
 *   - Name starts with target + " " or "(" — covers "Serena" →
 *     "Serena (Premium)" / "Serena (Enhanced)".
 *   - Single-word targets also check voiceURI segments — covers
 *     "Serena" → "com.apple.voice.premium.en-GB.Serena". Restricted
 *     to single-word targets so longer entries like "Microsoft Hazel
 *     - English (Great Britain)" don't false-positive on URIs.
 */
function findByCuratedNames(
  voices: SpeechSynthesisVoice[],
  names: ReadonlyArray<string>,
): SpeechSynthesisVoice | null {
  for (const target of names) {
    const lower = target.toLowerCase();
    const isSingleWord = !target.includes(" ");
    const hits = voices.filter((v) => {
      const nameLower = v.name.toLowerCase();
      if (nameLower === lower) return true;
      if (nameLower.startsWith(lower + " ")) return true;
      if (nameLower.startsWith(lower + "(")) return true;
      if (isSingleWord) {
        const uriLower = v.voiceURI.toLowerCase();
        // ".serena" guards against "Serena" matching a voice with
        // "renaissance" in the URI etc.
        if (uriLower.includes("." + lower)) return true;
      }
      return false;
    });
    if (hits.length === 0) continue;
    hits.sort((a, b) => tier(b) - tier(a));
    return hits[0];
  }
  return null;
}

function looksLikeEnGbFemale(value: string): boolean {
  const lower = value.toLowerCase();
  const isEnGb =
    lower.includes("uk english") ||
    lower.includes("english (united kingdom)") ||
    lower.includes("en-gb") ||
    lower.includes("british english");
  const isFemale = lower.includes("female") || /\b(kate|hazel|serena|martha|libby|sonia|susan)\b/.test(lower);
  return isEnGb && isFemale;
}

function inferLangFromVoiceName(name: string): string | null {
  const lower = name.toLowerCase();
  if (lower.includes("uk english") || lower.includes("english (united kingdom)") || lower.includes("en-gb")) {
    return "en-gb";
  }
  if (lower.includes("us english") || lower.includes("english (united states)") || lower.includes("en-us")) {
    return "en-us";
  }
  if (lower.includes("australian english") || lower.includes("english (australia)") || lower.includes("en-au")) {
    return "en-au";
  }
  return null;
}

